/**
 * shade-picker.js
 * Premium Nykaa-inspired shade selector for Shopify Vessel theme.
 * Reads variant data from JSON payload, renders custom shade UI,
 * and programmatically controls the native hidden variant-picker.
 *
 * NO jQuery. NO duplication of Shopify variant logic.
 */

const VISIBLE_SWATCH_LIMIT = 6;

class ShadePicker {
  /** @type {HTMLElement} */
  #root;
  /** @type {Object} */
  #data;
  /** @type {Array} */
  #uniqueShades = [];
  /** @type {string} */
  #selectedShade = '';

  // Cached DOM references
  #previewImg;
  #selectedNameEl;
  #swatchesRow;
  #moreBtn;
  #modalOverlay;
  #modalGrid;
  #modalCountEl;

  /** @param {HTMLElement} root */
  constructor(root) {
    this.#root = root;
    const jsonEl = root.querySelector('[data-shade-picker-json]');
    if (!jsonEl) return;

    try {
      this.#data = JSON.parse(jsonEl.textContent || '{}');
    } catch (e) {
      console.error('[ShadePicker] Failed to parse variant JSON', e);
      return;
    }

    this.#init();
  }

  #init() {
    this.#buildUniqueShades();
    if (!this.#uniqueShades.length) return;

    this.#cacheRefs();
    this.#setInitialShade();
    this.#renderSwatches();
    this.#renderModal();
    this.#bindEvents();
    this.#hideNativeShadeFieldset();
  }

  /**
   * Group variants by shade name, keeping only the first variant per shade.
   * This is the core deduplication step.
   */
  #buildUniqueShades() {
    const { variants, shadeOptionKey } = this.#data;
    if (!variants || !shadeOptionKey) return;

    const seen = new Set();
    this.#uniqueShades = [];

    for (const variant of variants) {
      const shadeName = variant[shadeOptionKey];
      if (shadeName && !seen.has(shadeName)) {
        seen.add(shadeName);
        this.#uniqueShades.push(variant);
      }
    }
  }

  #cacheRefs() {
    this.#previewImg = this.#root.querySelector('.shade-picker__preview-img');
    this.#selectedNameEl = this.#root.querySelector('.shade-picker__selected-name');
    this.#swatchesRow = this.#root.querySelector('.shade-picker__swatches-row');
    this.#moreBtn = this.#root.querySelector('.shade-picker__more-btn');
    this.#modalOverlay = this.#root.querySelector('.shade-modal-overlay');
    this.#modalGrid = this.#root.querySelector('.shade-modal__grid');
    this.#modalCountEl = this.#root.querySelector('.shade-modal__count');
  }

  /**
   * Determine which shade is initially selected based on the currently selected variant.
   */
  #setInitialShade() {
    const { currentVariantId, shadeOptionKey } = this.#data;
    const { variants } = this.#data;

    const current = variants.find((v) => String(v.id) === String(currentVariantId));
    if (current) {
      this.#selectedShade = current[shadeOptionKey];
    } else {
      this.#selectedShade = this.#uniqueShades[0]?.[shadeOptionKey] || '';
    }
  }

  /** Render all shade swatches; first 10 visible, rest hidden until expanded */
  #renderSwatches() {
    if (!this.#swatchesRow) return;
    this.#swatchesRow.innerHTML = '';

    const remaining = this.#uniqueShades.length - VISIBLE_SWATCH_LIMIT;

    this.#uniqueShades.forEach((shade, index) => {
      const btn = this.#makeSwatchBtn(shade, 'shade-picker__swatch', 80);
      // Mark swatches beyond the visible limit as overflow
      if (index >= VISIBLE_SWATCH_LIMIT) {
        btn.dataset.overflow = 'true';
      }
      this.#swatchesRow.appendChild(btn);
    });

    // Set dynamic grid columns (max 50) based on rendered swatches
    const totalRendered = Math.min(this.#uniqueShades.length, VISIBLE_SWATCH_LIMIT);
    const cols = Math.min(50, totalRendered);
    this.#swatchesRow.style.setProperty('--grid-columns', cols.toString());

    // +More button
    if (remaining > 0 && this.#moreBtn) {
      this.#moreBtn.textContent = `+${remaining}`;
      this.#moreBtn.classList.add('is-visible');
    } else if (this.#moreBtn) {
      this.#moreBtn.classList.remove('is-visible');
    }

    this.#updateSelectedState();
    this.#updatePreview();
  }

  /** Expand the swatches row to show all shades inline */
  #expandSwatches() {
    if (!this.#swatchesRow) return;
    this.#swatchesRow.classList.add('is-expanded');
    if (this.#moreBtn) {
      this.#moreBtn.classList.remove('is-visible');
    }
    // Update All Shades button to become a close button
    const allBtn = this.#root.querySelector('.shade-picker__all-btn');
    if (allBtn) {
      allBtn.classList.add('is-close');
      allBtn.setAttribute('aria-label', 'Close shades');
    }
  }

  /** Collapse the swatches row back to the initial 10 visible */
  #collapseSwatches() {
    if (!this.#swatchesRow) return;
    this.#swatchesRow.classList.remove('is-expanded');
    // Restore +more button if there are overflow shades
    const remaining = this.#uniqueShades.length - VISIBLE_SWATCH_LIMIT;
    if (remaining > 0 && this.#moreBtn) {
      this.#moreBtn.classList.add('is-visible');
    }
    // Restore All Shades button
    const allBtn = this.#root.querySelector('.shade-picker__all-btn');
    if (allBtn) {
      allBtn.classList.remove('is-close');
      allBtn.setAttribute('aria-label', 'All Shades');
    }
  }

  /** Render all shades in the modal grid */
  #renderModal() {
    if (!this.#modalGrid) return;
    this.#modalGrid.innerHTML = '';

    for (const shade of this.#uniqueShades) {
      const li = document.createElement('li');
      li.className = 'shade-modal__item';
      li.dataset.shade = shade[this.#data.shadeOptionKey];

      // Mark unavailable items on the list element too (for name styling)
      if (!shade.available) {
        li.classList.add('is-unavailable');
      }

      const btn = this.#makeSwatchBtn(shade, 'shade-modal__item-btn', 120);
      btn.setAttribute('aria-label', `Select shade ${shade[this.#data.shadeOptionKey]}`);

      const nameEl = document.createElement('span');
      nameEl.className = 'shade-modal__item-name';
      nameEl.textContent = shade[this.#data.shadeOptionKey];

      li.appendChild(btn);
      li.appendChild(nameEl);
      this.#modalGrid.appendChild(li);
    }

    if (this.#modalCountEl) {
      this.#modalCountEl.textContent = `(${this.#uniqueShades.length})`;
    }

    this.#updateModalSelectedState();
  }

  /**
   * Create a swatch button element.
   * @param {Object} shade - Variant data object
   * @param {string} className - CSS class
   * @param {number} imgWidth - Image size for Shopify CDN
   * @returns {HTMLButtonElement}
   */
  #makeSwatchBtn(shade, className, imgWidth) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = className;
    btn.dataset.shade = shade[this.#data.shadeOptionKey];

    // Mark unavailable (0 inventory) shades
    if (!shade.available) {
      btn.classList.add('is-unavailable');
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
    }

    // Liquid already sized the URLs; use them directly
    const imgUrl = shade.swatchImageUrl || shade.fallbackImageUrl || null;

    if (imgUrl) {
      const img = document.createElement('img');
      img.src = imgUrl;
      img.alt = shade[this.#data.shadeOptionKey];
      img.loading = 'lazy';
      img.width = imgWidth;
      img.height = imgWidth;
      btn.appendChild(img);
    } else {
      btn.classList.add(`${className}--placeholder`);
    }

    btn.setAttribute('aria-label', `Shade: ${shade[this.#data.shadeOptionKey]}${shade.available ? '' : ' (out of stock)'}`);
    return btn;
  }

  /** Get the large preview URL for a shade (uses pre-sized Liquid URL) */
  #getLargePreviewUrl(shade) {
    return shade.swatchImageUrlLarge || shade.swatchImageUrl || shade.fallbackImageUrl || null;
  }

  /**
   * Update the selected CSS class on swatches in the row.
   */
  #updateSelectedState() {
    if (!this.#swatchesRow) return;
    for (const btn of this.#swatchesRow.querySelectorAll('.shade-picker__swatch')) {
      const isSelected = btn.dataset.shade === this.#selectedShade;
      btn.classList.toggle('is-selected', isSelected);
      btn.setAttribute('aria-pressed', String(isSelected));
    }
  }

  /**
   * Update the selected CSS class on modal items.
   */
  #updateModalSelectedState() {
    if (!this.#modalGrid) return;
    for (const li of this.#modalGrid.querySelectorAll('.shade-modal__item')) {
      const isSelected = li.dataset.shade === this.#selectedShade;
      li.classList.toggle('is-selected', isSelected);
      const btn = li.querySelector('.shade-modal__item-btn');
      if (btn) {
        btn.classList.toggle('is-selected', isSelected);
        btn.setAttribute('aria-pressed', String(isSelected));
      }
    }
  }

  /**
   * Update the large preview image and selected name text.
   */
  #updatePreview() {
    const activeShade = this.#uniqueShades.find(
      (s) => s[this.#data.shadeOptionKey] === this.#selectedShade
    );

    // Update name badge
    if (this.#selectedNameEl) {
      this.#selectedNameEl.textContent = this.#selectedShade;
    }

    if (!this.#previewImg || !activeShade) return;

    const largeUrl = this.#getLargePreviewUrl(activeShade);

    if (largeUrl) {
      // Fade transition
      this.#previewImg.style.opacity = '0';
      this.#previewImg.src = largeUrl;
      this.#previewImg.onload = () => {
        this.#previewImg.style.opacity = '1';
      };
      this.#previewImg.alt = this.#selectedShade;
    } else {
      this.#previewImg.src = '';
      this.#previewImg.alt = '';
    }
  }

  /** Bind all event listeners */
  #bindEvents() {
    // Swatch row clicks
    this.#swatchesRow?.addEventListener('click', (e) => {
      const btn = /** @type {HTMLElement} */ (e.target.closest('.shade-picker__swatch'));
      if (!btn) return;
      this.#selectShade(btn.dataset.shade);
    });

    // Swatch keyboard: Enter/Space
    this.#swatchesRow?.addEventListener('keydown', (e) => {
      const btn = /** @type {HTMLElement} */ (e.target.closest('.shade-picker__swatch'));
      if (!btn) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.#selectShade(btn.dataset.shade);
      }
    });

    // Shade name button (left header) — opens modal
    this.#root.querySelector('.shade-picker__name-btn')?.addEventListener('click', () => {
      this.#openModal();
    });

    // Preview image box — also opens modal
    const previewWrap = this.#root.querySelector('.shade-picker__preview-wrap');
    previewWrap?.addEventListener('click', () => {
      this.#openModal();
    });
    previewWrap?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.#openModal();
      }
    });

    // "+X more" button — open modal
    this.#moreBtn?.addEventListener('click', () => {
      this.#openModal();
    });

    // Modal close button
    this.#root.querySelector('.shade-modal__close')?.addEventListener('click', () => {
      this.#closeModal();
    });

    // Overlay click to close
    this.#modalOverlay?.addEventListener('click', (e) => {
      if (e.target === this.#modalOverlay) this.#closeModal();
    });

    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.#modalOverlay?.classList.contains('is-open')) {
        this.#closeModal();
      }
    });

    // Modal grid clicks
    this.#modalGrid?.addEventListener('click', (e) => {
      const btn = /** @type {HTMLElement} */ (e.target.closest('.shade-modal__item-btn'));
      if (!btn) return;
      const shade = btn.dataset.shade;
      if (shade) {
        this.#selectShade(shade);
        this.#closeModal();
      }
    });

    // Modal grid keyboard
    this.#modalGrid?.addEventListener('keydown', (e) => {
      const btn = /** @type {HTMLElement} */ (e.target.closest('.shade-modal__item-btn'));
      if (!btn) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const shade = btn.dataset.shade;
        if (shade) {
          this.#selectShade(shade);
          this.#closeModal();
        }
      }
    });

    // Listen to native variant-picker changes (for when SIZE changes via native picker)
    // So we can keep the shade selection in sync
    const nativePicker = this.#getNativePicker();
    if (nativePicker) {
      nativePicker.addEventListener('change', (e) => {
        // Only process if it's NOT a shade change (to avoid infinite loops)
        if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) {
          // Re-read which shade is now active in the native picker
          this.#syncShadeFromNative();
        }
      });
    }
  }

  /**
   * Core: select a shade. Updates UI + triggers native variant picker.
   * @param {string} shadeName
   */
  #selectShade(shadeName) {
    if (shadeName === this.#selectedShade) return;

    // Prevent selecting unavailable shades
    const shade = this.#uniqueShades.find((s) => s[this.#data.shadeOptionKey] === shadeName);
    if (shade && !shade.available) return;

    this.#selectedShade = shadeName;

    this.#updateSelectedState();
    this.#updateModalSelectedState();
    this.#updatePreview();
    this.#triggerNativePicker(shadeName);
  }

  /**
   * Programmatically trigger Shopify's native variant picker for the selected shade.
   * Preserves current size selection when possible.
   * @param {string} shadeName
   */
  #triggerNativePicker(shadeName) {
    const nativePicker = this.#getNativePicker();
    if (!nativePicker) return;

    const { shadeOptionKey, sizeOptionKey } = this.#data;

    // Find currently selected size
    const currentSize = this.#getCurrentNativeSize(nativePicker);

    // Find the best matching variant: same shade + same size if possible
    const variants = this.#data.variants;
    let targetVariant = variants.find(
      (v) => v[shadeOptionKey] === shadeName && v[sizeOptionKey] === currentSize
    );

    // Fallback: same shade, any available size
    if (!targetVariant) {
      targetVariant = variants.find((v) => v[shadeOptionKey] === shadeName && v.available);
    }

    // Fallback: same shade, first size regardless of availability
    if (!targetVariant) {
      targetVariant = variants.find((v) => v[shadeOptionKey] === shadeName);
    }

    if (!targetVariant) return;

    // Find the radio input for this shade in the native shade fieldset
    const shadeFieldsetIndex = this.#data.shadeFieldsetIndex;
    const fieldsets = nativePicker.querySelectorAll('fieldset[data-fieldset-index]');
    const shadeFieldset = fieldsets[shadeFieldsetIndex];

    if (!shadeFieldset) return;

    // Find the radio with the matching shade value
    const radios = shadeFieldset.querySelectorAll('input[type="radio"]');
    let targetRadio = null;
    for (const radio of radios) {
      if (radio.value === shadeName) {
        targetRadio = radio;
        break;
      }
    }

    if (!targetRadio) return;

    // Programmatically check the radio and dispatch change event
    // This triggers Vessel's native VariantPicker.variantChanged()
    if (!targetRadio.checked) {
      targetRadio.checked = true;
      targetRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Get the current size value from the native picker.
   * @param {HTMLElement} nativePicker
   * @returns {string|null}
   */
  #getCurrentNativeSize(nativePicker) {
    const { sizeFieldsetIndex } = this.#data;
    const fieldsets = nativePicker.querySelectorAll('fieldset[data-fieldset-index]');
    const sizeFieldset = fieldsets[sizeFieldsetIndex];

    if (sizeFieldset) {
      const checked = sizeFieldset.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    }

    // Try select element
    const select = nativePicker.querySelector(`select[name*="${this.#data.sizeOptionName}"]`);
    return select ? select.value : null;
  }

  /**
   * After a native change event (e.g. user changed SIZE), re-sync selected shade
   * label and preview without re-triggering the native picker.
   */
  #syncShadeFromNative() {
    const nativePicker = this.#getNativePicker();
    if (!nativePicker) return;

    const { shadeFieldsetIndex, shadeOptionKey } = this.#data;
    const fieldsets = nativePicker.querySelectorAll('fieldset[data-fieldset-index]');
    const shadeFieldset = fieldsets[shadeFieldsetIndex];
    if (!shadeFieldset) return;

    const checked = shadeFieldset.querySelector('input[type="radio"]:checked');
    if (checked && checked.value !== this.#selectedShade) {
      this.#selectedShade = checked.value;
      this.#updateSelectedState();
      this.#updateModalSelectedState();
      this.#updatePreview();
    }
  }

  /**
   * Find the native <variant-picker> element for this product.
   * @returns {HTMLElement|null}
   */
  #getNativePicker() {
    const { productId } = this.#data;
    return document.querySelector(`variant-picker[data-product-id="${productId}"]`);
  }

  /**
   * Hide the native shade fieldset; leave size fieldset visible.
   * Adds .has-custom-shade-picker to variant-picker for CSS scoping.
   */
  #hideNativeShadeFieldset() {
    const nativePicker = this.#getNativePicker();
    if (!nativePicker) return;

    nativePicker.classList.add('has-custom-shade-picker');

    const { shadeFieldsetIndex } = this.#data;
    const fieldsets = nativePicker.querySelectorAll('fieldset[data-fieldset-index]');
    const shadeFieldset = fieldsets[shadeFieldsetIndex];
    if (shadeFieldset) {
      shadeFieldset.style.setProperty('display', 'none', 'important');
    }
  }

  /** Open the All Shades modal */
  #openModal() {
    if (!this.#modalOverlay) return;
    this.#updateModalSelectedState();
    this.#modalOverlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    // Focus management: focus the close button
    const closeBtn = this.#modalOverlay.querySelector('.shade-modal__close');
    if (closeBtn) {
      setTimeout(() => closeBtn.focus(), 50);
    }

    // Trap focus within modal
    this.#modalOverlay.setAttribute('role', 'dialog');
    this.#modalOverlay.setAttribute('aria-modal', 'true');
    this.#modalOverlay.setAttribute('aria-label', 'All Shades');
  }

  /** Close the All Shades modal */
  #closeModal() {
    if (!this.#modalOverlay) return;
    this.#modalOverlay.classList.remove('is-open');
    document.body.style.overflow = '';

    // Return focus to the "All Shades" button
    const allBtn = this.#root.querySelector('.shade-picker__all-btn');
    if (allBtn) allBtn.focus();
  }
}

// ── Initialise on DOM ready ──
function initShadePickers() {
  const pickers = document.querySelectorAll('[data-shade-picker]');
  for (const picker of pickers) {
    if (!picker._shadePickerInstance) {
      picker._shadePickerInstance = new ShadePicker(picker);
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initShadePickers);
} else {
  initShadePickers();
}

// Re-init after Shopify section renders (for theme editor)
document.addEventListener('shopify:section:load', initShadePickers);
